'use client';

import React, { forwardRef } from 'react';
import { NavigationMenuProps as NavProps } from '@radix-ui/react-navigation-menu';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';

import { ChevronDown } from '@flowind/icons';
import { clx, cva, DefaultProps, Selectors, useComponentDefaultProps } from '@/styles';
import { createPolymorphicComponent } from '@/utils/create-polymorphic-component';
import useStyles from './navigation-menu.styles';

export type NavMenuStylesNames = Selectors<typeof useStyles>;
export interface NavigationMenuProps extends DefaultProps<NavMenuStylesNames>, NavProps {}

const defaultProps: Partial<NavigationMenuProps> = {};

const _NavigationMenu = forwardRef<HTMLDivElement, NavigationMenuProps>((props, ref) => {
  const { className, classNames, children, ...others } = useComponentDefaultProps(
    'NavigationMenu',
    defaultProps,
    props,
  );

  const { classes, cx } = useStyles({}, { classNames, name: 'NavigationMenu' });

  return (
    <NavigationMenuPrimitive.Root className={cx(classes.root, className)} ref={ref} {...others}>
      {children}
      <NavigationMenuViewport />
    </NavigationMenuPrimitive.Root>
  );
}) as any;

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={clx('group flex flex-1 list-none items-center justify-center space-x-1', className)}
    {...props}
  />
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = cva({
  base: `group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium 
  transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground 
  focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50`,
});

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={clx(navigationMenuTriggerStyle(), 'group', className)}
    {...props}
  >
    {children}{' '}
    <ChevronDown
      className="relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={clx(
      `left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out 
      data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 
      data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 
      data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto`,
      className,
    )}
    {...props}
  />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

const NavigationMenuViewport = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <div className={clx('absolute left-0 top-full flex justify-center')}>
    <NavigationMenuPrimitive.Viewport
      className={clx(
        `origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden 
        rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in 
        data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 
        md:w-[var(--radix-navigation-menu-viewport-width)]`,
        className,
      )}
      ref={ref}
      {...props}
    />
  </div>
));
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;

const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    ref={ref}
    className={clx(
      `top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in 
      data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in`,
      className,
    )}
    {...props}
  >
    <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
  </NavigationMenuPrimitive.Indicator>
));
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName;

_NavigationMenu.displayName = 'NavigationMenu';
_NavigationMenu.List = NavigationMenuList;
_NavigationMenu.Item = NavigationMenuItem;
_NavigationMenu.Trigger = NavigationMenuTrigger;
_NavigationMenu.triggerStyle = navigationMenuTriggerStyle;
_NavigationMenu.Link = NavigationMenuLink;
_NavigationMenu.Content = NavigationMenuContent;
_NavigationMenu.Viewport = NavigationMenuViewport;
_NavigationMenu.Indicator = NavigationMenuIndicator;

export const NavigationMenu = createPolymorphicComponent<
  'NavigationMenu',
  NavigationMenuProps,
  {
    List: typeof NavigationMenuList;
    Item: typeof NavigationMenuItem;
    Trigger: typeof NavigationMenuTrigger;
    Link: typeof NavigationMenuLink;
    Content: typeof NavigationMenuContent;
    Viewport: typeof NavigationMenuViewport;
    Indicator: typeof NavigationMenuIndicator;
    triggerStyle: typeof navigationMenuTriggerStyle;
  }
>(_NavigationMenu);
